import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FindOptions, NotificationDTO, Subscriber } from '@app/models';
import { utils } from '@app/shared/utils';
import { ToastService } from '@teorema/patron';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../../services/notification.service';
import { SubscriberService } from '../../../../services/subscriber.service';

@Component({
  selector: 'patron-notification-list',
  templateUrl: './notification-list.component.html',
  standalone: false,
})
export class NotificationListComponent implements OnInit, AfterViewInit, OnDestroy {
  notifications: NotificationDTO[] = [];
  loading: boolean = false;
  currentPage = 1;
  lastPage = 1;
  totalItems = 0;
  company_group_id!: string;
  bw_user_id!: string;
  subscriber?: Subscriber;
  subscriptions: Subscription[] = [];

  @Input() isFromHeader!: boolean;
  @Input() findOptions!: FindOptions;
  @Input() sent: boolean = false;

  constructor(
    private notificationService: NotificationService,
    private toastService: ToastService,
    private subscriberService: SubscriberService
  ) {}

  ngOnInit(): void {
    this.company_group_id = utils.getCompanyGroup()?.id;
    this.bw_user_id = utils.getUser()?.id;

    if (this.isFromHeader) {
      this.findNotifications();

      this.subscriptions.push(
        this.subscriberService.currentSubscriberEmitter.subscribe((subscriber: Subscriber) => {
          if (!this.subscriber) {
            this.subscriber = subscriber;
            this.findNotifications();
          }
        })
      );

      this.subscriptions.push(
        this.notificationService.topicsChangedEventEmitter.subscribe((subscriber: Subscriber) => {
          this.findNotifications();
        })
      );
    }

    if (!this.sent) {
      this.subscriptions.push(
        this.notificationService.newNotificationEventEmitter.subscribe(
          (notification: NotificationDTO) => {
            if (!this.notifications) this.notifications = [];
            if (!this.notifications.find(n => n.notification_id === notification.notification_id)) {
              this.notifications.unshift(notification);
            }
          }
        )
      );
    }

    this.subscriptions.push(
      this.notificationService.readEventEmitter.subscribe((notification: NotificationDTO) => {
        if (!this.notifications) this.notifications = [];
        let index = this.notifications.findIndex(
          n => n.notification_receiver_id === notification.notification_receiver_id
        );

        if (index > -1) {
          this.notifications[index].read = true;
        }
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.sent) {
      this.subscriptions.push(
        this.notificationService.sentEventEmitter.subscribe((id: number) => {
          this.newNotificationSent(id);
        })
      );
    }
  }

  async findNotifications(findOptions?: FindOptions) {
    if (this.loading) return;
    this.loading = true;
    if (findOptions) this.findOptions = findOptions;

    if (!this.sent) {
      this.subscriber = await this.subscriberService.getCurrentSubscriber(true);
    }

    if (!this.findOptions.queryParams) {
      this.findOptions.queryParams = {};
    }

    if (this.subscriber) {
      this.findOptions.queryParams['subscriber_id'] = this.subscriber.id;
    }

    if (this.sent) {
      this.findOptions.queryParams['sent'] = true;
    }

    let response = await this.notificationService.findAll(this.findOptions).catch(e => {
      console.error(e);
      this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
    });

    if (response?.data) {
      this.notifications = response.data;
    }

    if (response?.total != null && response?.total >= 0) {
      this.totalItems = response.total;
    }

    this.loading = false;
  }

  async onRowClicked(notification: NotificationDTO) {
    this.notificationService.openNotificationEventEmitter.emit(notification);

    if (
      !notification.read &&
      (!notification.company_group_id ||
        this.bw_user_id === notification.bw_user_id ||
        (!notification.bw_user_id && notification.company_group_id === this.company_group_id))
    ) {
      let result: any = await this.notificationService
        .mark_as_read(this.company_group_id, this.bw_user_id, [notification])
        .catch(e => {
          console.error(e);
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao marcar a mensagem como lida',
          });
        });

      if (result?.length) {
        let index = this.notifications.findIndex(
          n => n.notification_id === notification.notification_id
        );

        if (index > -1) {
          this.notificationService.readEventEmitter.emit(this.notifications[index]);
        }
      }
    }
  }

  async newNotificationSent(id: number) {
    if (this.currentPage === 1) {
      let dtos = await this.notificationService.findById(id.toString());

      (dtos as unknown as NotificationDTO[]).forEach(dto => {
        if (
          !this.notifications.find(n => n.notification_receiver_id === dto.notification_receiver_id)
        ) {
          this.notifications.unshift(dto);
        }
      });
    }
  }

  get unreadCountText() {
    if (this.notifications) {
      let unreadCount: any = this.notifications.filter(n => !n.read).length;

      if (unreadCount) {
        unreadCount += '';

        if (
          this.totalItems > this.notifications.length &&
          !this.notifications[this.notifications.length - 1].read
        ) {
          unreadCount += '+';
        }

        return unreadCount;
      }
    }

    return '';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
